import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Copy, User, Package, Check, X } from 'lucide-react';
import { userApi, settingsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

export default function UserReferral() {
  const { user } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blcPrice, setBlcPrice] = useState(0.027);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const [response, settingsRes] = await Promise.all([
        userApi.getReferral(),
        settingsApi.get()
      ]);
      setReferralData(response.data);
      setBlcPrice(settingsRes.data.settings?.blc_price || 0.027);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(referralData?.referral_link || '');
    toast.success('Referral link copied!');
  };

  const usdToBLC = (usd) => blcPrice > 0 ? (Math.abs(usd) / blcPrice).toFixed(2) : '0.00';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const renderTree = (nodes, level = 1) => {
    if (!nodes || nodes.length === 0) return null;
    
    return (
      <div className="space-y-2 ml-4 border-l border-amber-500/20 pl-4">
        {nodes.map((node, idx) => (
          <div key={node.id || idx}>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-black/30 hover:bg-black/50 transition-colors">
              <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                <User className="w-4 h-4 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{node.email}</p>
                <p className="text-xs text-zinc-500">{node.full_name}</p>
              </div>
              <div className="text-right">
                <span className={`badge ${node.is_active ? 'badge-success' : 'badge-danger'}`}>
                  {node.is_active ? 'Active' : 'Inactive'}
                </span>
                <p className="text-xs text-zinc-500 mt-1">L{level}</p>
              </div>
            </div>
            {node.downline && node.downline.length > 0 && renderTree(node.downline, level + 1)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-8" data-testid="referral-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-purple-500/10">
          <Users className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <h1 className="text-2xl text-white font-medium">Team & Referrals</h1>
          <p className="text-sm text-zinc-500">Grow your network and earn commissions</p>
        </div>
      </div>

      {/* Referral Link */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-6 border-amber-500/20"
      >
        <h2 className="text-white font-medium mb-4">Your Referral Link</h2>
        <div className="flex gap-2">
          <div className="flex-1 bg-black/30 rounded-lg px-4 py-3 font-mono text-sm text-amber-400 truncate">
            {referralData?.referral_link}
          </div>
          <button
            onClick={copyLink}
            className="px-4 py-3 rounded-lg gold-btn flex items-center gap-2"
            data-testid="copy-referral-link"
          >
            <Copy className="w-4 h-4" /> Copy
          </button>
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          Share this link with friends. They'll be automatically connected to your network.
        </p>
      </motion.div>

      {/* Level Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map((level) => (
          <motion.div
            key={level}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: level * 0.05 }}
            className="glass-card p-4 text-center"
          >
            <p className="text-xs text-zinc-500 mb-1">Level {level}</p>
            <p className="text-2xl text-white font-medium">
              {referralData?.level_counts?.[`level${level}`] || 0}
            </p>
            <p className="text-xs text-green-400 mt-1">
              ${(referralData?.commission_by_level?.[`level${level}`] || 0).toFixed(2)}
              <span className="text-green-400/70 ml-1">({usdToBLC(referralData?.commission_by_level?.[`level${level}`] || 0)} BLC)</span>
            </p>
          </motion.div>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="stat-card border-l-2 border-purple-500">
          <p className="text-xs text-zinc-500 uppercase">Total Team</p>
          <p className="text-2xl text-white font-medium mt-1">{referralData?.total_team || 0}</p>
        </div>
        <div className="stat-card border-l-2 border-green-500">
          <p className="text-xs text-zinc-500 uppercase">Total Commissions</p>
          <p className="text-2xl text-white font-medium mt-1">
            ${(referralData?.total_commissions || 0).toFixed(2)}
            <span className="text-amber-400 text-sm ml-2">({usdToBLC(referralData?.total_commissions || 0)} BLC)</span>
          </p>
        </div>
        <div className="stat-card border-l-2 border-amber-500">
          <p className="text-xs text-zinc-500 uppercase">Direct Referrals</p>
          <p className="text-2xl text-white font-medium mt-1">
            {referralData?.level_counts?.level1 || 0}
          </p>
        </div>
      </div>

      {/* Team Tree */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass-card p-6"
      >
        <h2 className="text-white font-medium mb-4">Team Tree</h2>
        {referralData?.tree && referralData.tree.length > 0 ? (
          <div className="max-h-[500px] overflow-y-auto">
            {renderTree(referralData.tree)}
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No team members yet</p>
            <p className="text-xs text-zinc-500 mt-1">Share your referral link to start building your team</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
